/**
 * **동료 고유 기술**(Todo10 4단계, 사용자가 고른 권장안): 단추 없이, 조건이 맞으면 동료가 제 분대에 스스로 건다. 유형이
 * 결을 정한다 — 용장은 타격, 지장은 사기, 덕장은 덜 맞기. 같은 유형이라도 나라마다 **언제** 쓰는지가 다르다(고구려는 맞붙는
 * 순간, 백제는 몰렸을 때, 신라는 섞여 있다). 동료가 쓰러지면 꺼진다.
 *
 * 기술 레벨은 동료 레벨에서 나온다(25·50·75에 한 단계씩, 1~4) — 명부에 따로 싣지 않는다. 동료 레벨(`companionLevels`)과
 * 동료 id가 이미 명부에 있고, 거기서 나오는 순수 함수라 서버가 같은 답을 낸다.
 *
 * 한 장치로 아홉을 만든다(발동 조건 넷 × 효과 셋). 기술마다 규칙을 따로 짜면 판정 코드가 아홉 갈래로 갈라진다.
 * 수치는 모두 정수다.
 */

/** 언제 쓰는가. 번호를 바꾸지 않는다 — 시뮬레이션 상태에 든다. */
export const MateTrigger = {
  /** 분대가 적과 맞붙을 때: 분대 병사 셋에 하나가 표적을 쥐었다(병사가 없으면 동료가 표적을 쥐었다). */
  Engage: 1,
  /** 분대 병사가 처음의 `MATE_LOSS_PCT`% 이하로 줄었다. */
  Half: 2,
  /** 동료의 둘레(`radius`) 안에서 아군 병사가 쓰러졌다 — 어느 분대의 병사든. 그 순간 둘레 안의 아군에게 건다. */
  Fallen: 3,
  /** 동료 자신의 체력이 `MATE_HURT_PCT`% 이하다. */
  Hurt: 4,
} as const;
export type MateTrigger = (typeof MateTrigger)[keyof typeof MateTrigger];

/**
 * 발동의 문턱. 처음에는 사기 60%·병사 절반·체력 절반이었는데, 재 보니(`npm run mate-probe`) 사기 조건은 서른 판에 한 번도
 * 안 걸리고(80%로 올려도 0번 — 지장이 맡는 분대는 사기가 높다) 병사 절반은 열 판에 한두 번, 체력 절반은 레벨이 높은
 * 동료에게 걸리지 않았다 — 가진 줄도 모르는 기술이 된다. 사기 조건은 "병사가 쓰러지면"으로 바꾸고 나머지는 낮췄다.
 *
 * **한 번 더 낮췄다**(선택지 균형 묶음, 2026-10-03 — 기준: 한 판에 0.5~2번). 75%·70%에서 깊이 15(표준 편성, 시드 30)에
 * 계백 0.3·흑치상지 0.4번, 깊이 10에 알천 0.0번이었다. 85%·85%로 흑치상지 0.5, 알천 2.1(깊이 15), 깊이 20은 아홉 모두
 * 0.6~1.1번. 계백은 깊이 15에 0.3번 그대로다 — 앞 분대가 잘 잃지 않는 판에서 "몰리면 쓰는" 기술이 안 쓰이는 것은 결대로다.
 */
/** 병사: 남은 병사 ÷ 처음 병사(%)가 이 이하로. */
export const MATE_LOSS_PCT = 85;
/** 체력: 동료의 체력 ÷ 최대 체력(%)이 이 이하로. */
export const MATE_HURT_PCT = 85;
export const MATE_SKILL_LEVEL_MAX = 4;
/** 기술 레벨이 오르는 동료 레벨. */
export const MATE_SKILL_LEVELS: readonly number[] = [25, 50, 75];

/** 동료 레벨의 기술 레벨(1~4). */
export function mateSkillLevel(companionLevel: number): number {
  let lv = 1;
  for (const at of MATE_SKILL_LEVELS) if (companionLevel >= at) lv++;
  return lv;
}

export interface MateSkillDef {
  /** 동료 id. */
  readonly id: string;
  readonly name: string;
  readonly hanja: string;
  readonly trigger: MateTrigger;
  /** 분대의 타격 +%(레벨 1)와 레벨마다 더하는 몫. 0이면 없다. */
  readonly atk: number;
  readonly atkStep: number;
  /** 분대가 받는 피해 −%(레벨 1)와 레벨마다 더하는 몫. 0이면 없다. */
  readonly cut: number;
  readonly cutStep: number;
  /** 쓰는 순간 분대 병사의 사기를 채우고 달아나던 병사를 돌려세우는가. */
  readonly rally: boolean;
  /** 지속과 재사용 대기(틱, 30Hz). */
  readonly ticks: number;
  readonly cd: number;
  /**
   * 둘레에 거는 기술의 반경(px, 레벨 1)과 레벨마다 더하는 몫. 없으면 제 분대에 거는 기술이다. 지장의 계책·충언이 쓴다 —
   * 지장은 제 분대가 아니라 둘레의 아군을 본다.
   */
  readonly radius?: number;
  readonly radiusStep?: number;
}

const T = MateTrigger;
// 맞붙으면 쓰는 셋(온달·명림답부·김유신)은 대기가 25초다 — 20초에서 깊이 15에 한 판 2.2~2.5번으로 기준(0.5~2번)을 넘었다
// (선택지 균형 묶음). 25초로 1.5~2.2번.

export const MATE_SKILLS: readonly MateSkillDef[] = [
  // 고구려 — 맞붙는 순간의 한 수.
  { id: 'go_ondal', name: '선봉', hanja: '先鋒', trigger: T.Engage, atk: 20, atkStep: 5, cut: 0, cutStep: 0, rally: false, ticks: 150, cd: 750 },
  { id: 'go_euldudji', name: '계책', hanja: '計策', trigger: T.Fallen, atk: 0, atkStep: 0, cut: 10, cutStep: 5, rally: true, ticks: 150, cd: 750, radius: 180, radiusStep: 20 },
  { id: 'go_myeongnim', name: '청야', hanja: '淸野', trigger: T.Engage, atk: 0, atkStep: 0, cut: 15, cutStep: 5, rally: false, ticks: 180, cd: 750 },
  // 백제 — 몰렸을 때 버틴다.
  { id: 'bj_gyebaek', name: '결사', hanja: '決死', trigger: T.Half, atk: 30, atkStep: 10, cut: 0, cutStep: 0, rally: true, ticks: 240, cd: 900 },
  { id: 'bj_seongchung', name: '충언', hanja: '忠言', trigger: T.Fallen, atk: 10, atkStep: 5, cut: 0, cutStep: 0, rally: true, ticks: 150, cd: 750, radius: 180, radiusStep: 20 },
  { id: 'bj_heukchi', name: '재기', hanja: '再起', trigger: T.Half, atk: 0, atkStep: 0, cut: 20, cutStep: 5, rally: true, ticks: 240, cd: 900 },
  // 신라.
  { id: 'sl_jukjuk', name: '불굴', hanja: '不屈', trigger: T.Hurt, atk: 25, atkStep: 10, cut: 0, cutStep: 0, rally: false, ticks: 180, cd: 750 },
  { id: 'sl_yusin', name: '독려', hanja: '督勵', trigger: T.Engage, atk: 10, atkStep: 3, cut: 10, cutStep: 3, rally: false, ticks: 150, cd: 750 },
  { id: 'sl_alcheon', name: '호위', hanja: '護衛', trigger: T.Hurt, atk: 0, atkStep: 0, cut: 20, cutStep: 5, rally: false, ticks: 180, cd: 750 },
];

export const MATE_SKILL_BY_ID: ReadonlyMap<string, MateSkillDef> = new Map(MATE_SKILLS.map((s) => [s.id, s]));

/** 그 레벨의 타격 +%와 받는 피해 −%, 둘레 기술이면 반경(px, 아니면 0). */
export function mateSkillPcts(def: MateSkillDef, level: number): { atk: number; cut: number; radius: number } {
  const k = Math.min(MATE_SKILL_LEVEL_MAX, Math.max(1, Math.floor(level))) - 1;
  return {
    atk: def.atk > 0 ? def.atk + def.atkStep * k : 0,
    cut: def.cut > 0 ? def.cut + def.cutStep * k : 0,
    radius: def.radius ? def.radius + (def.radiusStep ?? 0) * k : 0,
  };
}

const WHEN: Record<MateTrigger, string> = {
  [T.Engage]: '분대가 적과 맞붙으면',
  // 문턱(`MATE_LOSS_PCT`)에서 낸다 — 75%일 때 "넷에 하나"로 손으로 적어 두었더니 85%로 바꾸며 글만 남을 뻔했다.
  // 판정은 `남은 × 100 ≤ 처음 × MATE_LOSS_PCT`(world.ts)라 경계를 포함한다 — "넘게"가 아니라 "이상"(리뷰).
  [T.Half]: `분대 병사를 ${100 - MATE_LOSS_PCT}% 이상 잃으면`,
  [T.Fallen]: '둘레에서 아군 병사가 쓰러지면',
  [T.Hurt]: `자신의 체력이 ${MATE_HURT_PCT}% 이하가 되면`,
};

/** 한 줄 설명(레벨 수치를 넣는다). 화면이 읽는다. */
export function mateSkillText(def: MateSkillDef, level: number): string {
  const p = mateSkillPcts(def, level);
  const parts: string[] = [];
  if (def.rally) parts.push('사기를 채우고 달아나던 병사를 돌려세운다');
  if (p.atk > 0) parts.push(`타격 +${p.atk}%`);
  if (p.cut > 0) parts.push(`받는 피해 −${p.cut}%`);
  const sec = (t: number): string => `${Math.round((t / 30) * 10) / 10}초`;
  // 둘레 기술은 누구에게 거는지와 반경을 적는다.
  const when = p.radius > 0 ? `둘레 ${p.radius}px 안에서 아군 병사가 쓰러지면` : WHEN[def.trigger];
  const whom = p.radius > 0 ? '둘레의 아군에게: ' : '';
  return `${when} 스스로 쓴다 — ${whom}${parts.join(', ')}(${sec(def.ticks)}). ${sec(def.cd)}에 한 번.`;
}
